import { useState, forwardRef, useRef, KeyboardEvent, useMemo, useCallback } from 'react';
import { Autocomplete, Typography } from '@mui/material';
import { Popover } from 'shared-ui';
import { useMeasure } from 'react-use';

import { ReactComponent as DropdownIcon } from 'assets/icons/dropdown.svg';
import { ReactComponent as RemoveIcon } from 'assets/icons/remove.svg';
import { Table } from 'ui/table/Table';
import { MultipleSelectProps, TreeOption, TreeSelectOption } from '../OrganizationsTreeSelect.types';
import { transformTreeToFlatArray } from 'helpers/transformTreeToFlatArray';
import { transformTreeToFlatArrayWithPath } from 'helpers/transformTreeToFlatArrayWithPath';
import { findTreeOptionById } from 'helpers/findTreeOptionById';

import * as Styled from './TreeMultiSelect.styles';
import { TreeMultiSelectBody } from './treeMultiSelectBody/TreeMultiSelectBody';
import { GroupedTreeMultiSelectBody } from './groupedTreeMultiSelectBody/GroupedTreeMultiSelectBody';

const TAGS_LIMIT = 1;
const FIRST_LEVEL_SPACE_WIDTH = 60;
const SECOND_LEVEL_SPACE_WIDTH = 110;
const MAX_POPOVER_HEIGHT = 350;
const MIN_POPOVER_HEIGHT = 150;
const VIEWPORT_MARGIN = 16;

export const TreeMultiSelect = forwardRef<HTMLInputElement, MultipleSelectProps>(
  (
    {
      options,
      value: originalValue,
      onChange,
      label,
      placeholder,
      isFilterType,
      inputVariant,
      extraGroupProps,
      inheritChildren = false,
      ...props
    },
    ref,
  ) => {
    const [popoverEl, setPopoverEl] = useState<HTMLDivElement | undefined>(undefined);

    const autocompleteRef = useRef<HTMLInputElement | undefined>(undefined);

    const onClosePopover = () => setPopoverEl(undefined);

    const getPopoverMaxHeight = useCallback(() => {
      if (!popoverEl) return MAX_POPOVER_HEIGHT;
      const rect = popoverEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      return Math.min(MAX_POPOVER_HEIGHT, Math.max(spaceBelow, MIN_POPOVER_HEIGHT));
    }, [popoverEl]);

    const updateInheritedFlags = (options: TreeOption[], value: TreeSelectOption[]): TreeSelectOption[] => {
      const flatTree = transformTreeToFlatArrayWithPath(options);
      const selectedIds = value.map((item) => item.id);

      return value.map((val) => {
        const match = flatTree.find(({ item }) => item.id === val.id);
        if (!match) return { ...val, isInherited: false };

        const isInherited = match.path.some((parentId) => selectedIds.includes(parentId));
        return {
          ...val,
          isInherited,
        };
      });
    };

    const value = useMemo(() => {
      return inheritChildren ? updateInheritedFlags(options, originalValue ?? []) : originalValue;
    }, [inheritChildren, options, originalValue]);

    const isOptionSelected = (optionId: string) =>
      value ? value.some((selectedOption) => selectedOption.id === optionId) : false;

    const selectItemWithInheritation = (option: TreeOption) => {
      const isSelected = isOptionSelected(option.id);

      const allOptions = transformTreeToFlatArray([option]);
      const optionsIds = allOptions.map((d) => d.id);

      const optionsValue =
        value?.map(({ id, nazwa, kod }) => ({
          id,
          nazwa,
          kod,
        })) || [];

      if (isSelected) {
        const newValue = optionsValue.filter((selectedOption) => !optionsIds.includes(selectedOption.id));
        onChange(newValue);
      } else {
        const itemsToAdd = allOptions.map(({ id, nazwa, kod }) => ({
          id,
          nazwa,
          kod,
        }));

        const existingIds = optionsValue.map((item) => item.id);
        const uniqueItemsToAdd = itemsToAdd.filter((item) => !existingIds.includes(item.id));

        onChange([...optionsValue, ...uniqueItemsToAdd]);
      }
    };

    const selectItem = (option: TreeOption) => {
      const isSelected = isOptionSelected(option.id);

      const optionsValue = value?.map(({ id, nazwa, kod }) => ({ id, nazwa, kod })) || [];

      if (isSelected) {
        onChange(optionsValue.filter((selectedOption) => selectedOption.id !== option.id));
        return;
      }
      onChange([...optionsValue, option]);
    };

    const onSelectItem = (option: TreeOption) => {
      if (inheritChildren) {
        selectItemWithInheritation(option);
      } else {
        selectItem(option);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        setPopoverEl(e.currentTarget as HTMLDivElement);
      }

      if (e.key === 'Backspace') {
        const nonInheritedItems = value?.filter((item) => !item.isInherited) || [];
        if (nonInheritedItems.length) {
          const lastSelected = nonInheritedItems[nonInheritedItems.length - 1];

          const fullOption = findTreeOptionById(options, lastSelected.id);

          if (fullOption) {
            onSelectItem(fullOption);
          }
        }
      }

      if (e.key === 'Tab') return;

      e.preventDefault();
      e.stopPropagation();
    };

    const [tagRef, { width }] = useMeasure<HTMLDivElement>();

    const renderTags = (selectedValues: TreeSelectOption[]) => {
      const flatTree = transformTreeToFlatArrayWithPath(options);

      const sortedSelectedValues = selectedValues.sort((a, b) => {
        const aInfo = flatTree.find(({ item }) => item.id === a.id);
        const bInfo = flatTree.find(({ item }) => item.id === b.id);

        const aDepth = aInfo?.path.length ?? 0;
        const bDepth = bInfo?.path.length ?? 0;

        if (aDepth !== bDepth) return aDepth - bDepth;

        if (!a.kod || !b.kod) return 0;
        return a.kod.localeCompare(b.kod);
      });

      return sortedSelectedValues.length === TAGS_LIMIT ? (
        <Styled.Tag style={{ maxWidth: width - FIRST_LEVEL_SPACE_WIDTH }}>
          <Typography noWrap sx={{ fontWeight: 'inherit' }}>
            {sortedSelectedValues[0].nazwa}
          </Typography>
        </Styled.Tag>
      ) : (
        <>
          <Styled.Tag style={{ maxWidth: width - SECOND_LEVEL_SPACE_WIDTH }}>
            <Typography noWrap sx={{ fontWeight: 'inherit' }}>
              {sortedSelectedValues[0].nazwa}
            </Typography>
          </Styled.Tag>
          <Styled.Tag>+{sortedSelectedValues.length - TAGS_LIMIT}</Styled.Tag>
        </>
      );
    };

    return (
      <>
        {isFilterType ? (
          <Table.HeaderItem>
            <Styled.CellHeaderItem $isActive={!!popoverEl} onClick={(e) => setPopoverEl(e.currentTarget)}>
              {placeholder}
              {!!value && !!value.length ? (
                <RemoveIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                />
              ) : (
                <DropdownIcon />
              )}
            </Styled.CellHeaderItem>
          </Table.HeaderItem>
        ) : (
          <Autocomplete
            data-testid={label}
            limitTags={value && value.length > TAGS_LIMIT ? 0 : TAGS_LIMIT}
            disableClearable
            options={value || []}
            getOptionLabel={(option) => option.nazwa}
            value={value || []}
            isOptionEqualToValue={(option, value) => option.nazwa === value.nazwa}
            renderTags={renderTags}
            ref={autocompleteRef}
            getLimitTagsText={(tagsCount) => tagsCount}
            {...props}
            renderInput={(params) => (
              <div onClick={(e) => !props.disabled && setPopoverEl(e.currentTarget)}>
                <Styled.Input
                  {...params}
                  required={props.required}
                  label={label}
                  variant={inputVariant || 'filled'}
                  InputLabelProps={{ shrink: false }}
                  InputProps={{ ...params.InputProps, ref: tagRef }}
                  placeholder={value && !!value.length ? '' : placeholder}
                  rightIcon={<Styled.SelectArrow />}
                  withoutHelperText
                  inputRef={ref}
                  onKeyDown={onKeyDown}
                />
              </div>
            )}
          />
        )}

        <Popover
          disableScrollLock
          anchorEl={popoverEl}
          onClose={onClosePopover}
          PaperProps={{
            style: {
              marginTop: isFilterType ? 8 : 0,
              width: autocompleteRef.current?.clientWidth,
              minWidth: isFilterType ? 325 : autocompleteRef.current?.clientWidth,
              maxHeight: getPopoverMaxHeight(),
              overflow: 'auto',
            },
          }}
        >
          {extraGroupProps ? (
            <GroupedTreeMultiSelectBody
              extraGroupProps={extraGroupProps}
              onSelectItem={onSelectItem}
              isOptionSelected={isOptionSelected}
              clearSelectedItems={() => onChange([])}
              isPopoverOpen={!!popoverEl}
              renderOrganizationsGroupBody={() => (
                <TreeMultiSelectBody
                  options={options}
                  onSelectItem={onSelectItem}
                  value={value}
                  onClosePopover={onClosePopover}
                  isOptionSelected={isOptionSelected}
                  inheritChildren={inheritChildren}
                />
              )}
            />
          ) : (
            <TreeMultiSelectBody
              options={options}
              onSelectItem={onSelectItem}
              value={value}
              onClosePopover={onClosePopover}
              isOptionSelected={isOptionSelected}
              inheritChildren={inheritChildren}
            />
          )}
        </Popover>
      </>
    );
  },
);
